import { useMemo, useState } from 'react';
import { IPoiItem } from '../../../domain/models/IPoiItem';
import { sortPoiItemsAlphabetically } from '../../../domain/utils/sortPoiItemsAlphabetically';
import { PoiOpeningHours } from '../models/PoiOpeningHours';
import { PoiSecurityZoneItems } from '../models/PoiSecurityZone';
import { ALL_POI_TYPES_FILTER_RESET } from '../../../domain/utils/getPoiTypeKeywords';
import { ALL_LOCATIONS_FILTER_RESET } from '../../../domain/utils/getAllTerminalLocationItems';
import { PoiListScope, scopeToCategoryMap } from '../models/PoiListScope';
import { isPoiOpen } from '../utils/isPoiOpen';

export function usePoiStore(initialData: IPoiItem[]) {
	const [scope, setScope] = useState<PoiListScope>(PoiListScope.All);
	const [searchQuery, setSearchQuery] = useState<string | undefined>('');
	const [location, setLocation] = useState<string>();
	const [openingHours, setOpeningHours] = useState<string>();
	const [securityZone, setSecurityZone] = useState<string>();
	const [type, setType] = useState<string>();

	const orderedPoiItems = useMemo(
		() => sortPoiItemsAlphabetically(initialData),
		[initialData],
	);

	const filteredData = useMemo(() => {
		let filteredPoiData = orderedPoiItems;

		if (scope !== PoiListScope.All) {
			const scopeToFilter = scopeToCategoryMap[scope].toLowerCase();
			filteredPoiData = filteredPoiData.filter((poi) =>
				poi.category.toLowerCase().includes(scopeToFilter),
			);
		}

		if (searchQuery) {
			const lowerCaseQuery = searchQuery.toLowerCase();

			filteredPoiData = filteredPoiData.filter((poi) => {
				const nameMatches = poi.name?.toLowerCase().includes(lowerCaseQuery);
				const tagMatches = poi.tags.some(
					(tag) =>
						tag.isUserSearchable &&
						tag.name.toLowerCase().includes(lowerCaseQuery),
				);

				return nameMatches || tagMatches;
			});
		}

		if (location && location !== ALL_LOCATIONS_FILTER_RESET) {
			filteredPoiData = filteredPoiData.filter(
				(poi) => poi?.terminal === location,
			);
		}

		if (openingHours && openingHours !== PoiOpeningHours.AllHours) {
			filteredPoiData = filteredPoiData.filter((poi) =>
				isPoiOpen(poi?.operationHours),
			);
		}

		if (securityZone && securityZone !== PoiSecurityZoneItems.All) {
			const filterAfterSecurity =
				securityZone === PoiSecurityZoneItems.AfterSecurity;

			filteredPoiData = filteredPoiData.filter(
				(poi) => poi?.isAfterSecurity === filterAfterSecurity,
			);
		}

		if (type && type !== ALL_POI_TYPES_FILTER_RESET) {
			filteredPoiData = filteredPoiData.filter((poi) => {
				const displayTagNames = poi.tags
					.filter((tag) => tag.isDisplayed)
					.map((tag) => tag.name.toLowerCase());
				return displayTagNames.includes(type.toLowerCase());
			});
		}

		return filteredPoiData;
	}, [
		initialData,
		location,
		openingHours,
		securityZone,
		type,
		scope,
		searchQuery,
	]);

	return {
		data: filteredData,
		scope,
		setLocation,
		setOpeningHours,
		setSecurityZone,
		setType,
		setScope,
		setSearchQuery,
	};
}
